/**
 * SponsorRewardsPanel — Affiche les récompenses de parrainage reçues par le client connecté.
 *
 * Affichée dans l'onglet "Compte" du portail. N'apparaît que si le client a au moins 1 récompense.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Gift, Calendar, Wallet, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function SponsorRewardsPanel() {
  const [data, setData] = useState({ rewards: [], total_extended_months: 0, total_credited: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('client_portal_token');
      const r = await fetch(`${API}/api/client-portal/sponsor-rewards`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
    } catch (e) {
      // silencieux
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !data.rewards?.length) return null;

  return (
    <div
      className="rounded-2xl p-4"
      style={{ backgroundColor: 'rgba(255,255,255,0.92)', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
      data-testid="sponsor-rewards-panel"
    >
      <h4 className="font-bold uppercase mb-3 flex items-center gap-1.5" style={{ fontSize: '11px', color: '#8a6d1e', letterSpacing: '0.08em' }}>
        <Gift size={11} style={{ color: '#b78a1e' }} />
        Mes parrainages ({data.rewards.length})
      </h4>

      {/* Totaux si multiples récompenses */}
      {(data.total_extended_months > 0 || data.total_credited > 0) && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {data.total_extended_months > 0 && (
            <div className="bg-[#fdfcf6] rounded p-2 border border-[#d4a850]/40 text-center">
              <div className="text-lg font-bold text-[#0d1a12]">+{data.total_extended_months}<span className="text-xs ml-0.5">mois</span></div>
              <div className="text-[10px] uppercase tracking-wider text-[#8a6d1e]">Prolongations</div>
            </div>
          )}
          {data.total_credited > 0 && (
            <div className="bg-[#fdfcf6] rounded p-2 border border-[#d4a850]/40 text-center">
              <div className="text-lg font-bold text-[#0d1a12]">+{data.total_credited.toFixed(2)}<span className="text-xs ml-0.5">€</span></div>
              <div className="text-[10px] uppercase tracking-wider text-[#8a6d1e]">Crédités</div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-1.5">
        {data.rewards.map((r) => (
          <div
            key={r.id}
            className="flex items-start gap-2 p-2 bg-[#fdfcf6]/60 rounded border border-[#d4a850]/30"
            data-testid={`sponsor-reward-${r.id}`}
          >
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#fdfcf6] border border-[#d4a850]/50 flex items-center justify-center">
              {r.reward_type === 'subscription_extension' ? (
                <Calendar size={13} className="text-[#b78a1e]" />
              ) : (
                <Wallet size={13} className="text-[#b78a1e]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-[#0d1a12]">
                {r.reward_type === 'subscription_extension'
                  ? `Prolongation parrainage · +${Math.round(r.reward_value)} mois`
                  : `Crédit parrainage · +${Number(r.reward_value).toFixed(2)} €`}
              </div>
              <div className="text-[10px] text-neutral-500 flex items-center gap-1 flex-wrap">
                <Users size={9} />
                <span>{r.referred_client_name}</span>
                <span className="text-neutral-300">·</span>
                <span>{r.contract_label}</span>
              </div>
              {r.applied_at && (
                <div className="text-[10px] text-neutral-400 mt-0.5">
                  {new Date(r.applied_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
